import { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import api from "../lib/api";
import { toast } from "sonner";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import SEO from "../components/SEO";
import {
  Package, Image as ImageIcon, Stack, UploadSimple, DownloadSimple, Plus, PencilSimple, Trash,
  X, Check, MagnifyingGlass, CaretDown, CaretUp, Warning, CheckCircle, Star, Tag, Lightning, ToggleLeft, ToggleRight,
  Folder, Folders, FileXls
} from "@phosphor-icons/react";
import BrandMasterTab, { BrandQuickAdd } from "../components/admin/BrandMasterTab";
import LeadSourcesTab from "../components/admin/LeadSourcesTab";
import FollowupStatusesTab from "../components/admin/FollowupStatusesTab";

const TABS = [
  { key: "products", label: "Products", icon: Package },
  { key: "brands", label: "Brands", icon: Tag },
  { key: "bulk", label: "Bulk Ops", icon: UploadSimple },
  { key: "categories", label: "Categories", icon: Folders },
  { key: "tags", label: "Tags", icon: Tag },
  { key: "banners", label: "Banners", icon: ImageIcon },
  { key: "combos", label: "Combos & Kits", icon: Stack },
  { key: "lead-sources", label: "Lead Sources", icon: Tag },
  { key: "followup-statuses", label: "Follow-up Statuses", icon: Tag },
];

// Tabs MD users may see (Lead Sources + Follow-up Statuses only — keeps Admin Catalogue access narrow).
const MD_ONLY_TABS = new Set(["lead-sources", "followup-statuses"]);

const AdminCatalogue = ({ user, logout }) => {
  const location = useLocation();
  const role = (user?.role || "").toLowerCase();
  const canEdit = ["admin", "manager", "marketing_head"].includes(user?.role);
  const isMD = role === "md";
  const canAccessPage = canEdit || isMD;
  const visibleTabs = useMemo(
    () => (isMD && !canEdit ? TABS.filter((t) => MD_ONLY_TABS.has(t.key)) : TABS),
    [isMD, canEdit]
  );
  const initialTab = useMemo(() => {
    const t = new URLSearchParams(location.search).get("tab");
    const allowed = visibleTabs.some((x) => x.key === t);
    return allowed ? t : (visibleTabs[0]?.key || "products");
  }, [location.search, visibleTabs]);
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);

  // Measure the sticky page header (module header + tabs) so child sticky layers
  // (toolbar, table header) can dock to its REAL height — responsive to wrapping.
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const update = () => setHeaderH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeTab]);

  if (!canAccessPage) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar user={user} logout={logout} />
        <div className="flex-1 ml-64 flex items-center justify-center p-8">
          <div className="text-center">
            <Warning size={40} className="mx-auto text-amber-500 mb-3" />
            <div className="flex items-center"><h1 className="text-xl font-bold mb-1">Admin access required</h1><HelpButton moduleKey="product" /></div>
            <p className="text-sm text-slate-500">Only Admin / Manager / Marketing Head can manage the catalogue.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <SEO title="Catalogue Admin — SwifTech India" description="Manage products, banners, bulk ops & combos." />
      <div className="flex-1 ml-64">
        <div className="max-w-7xl mx-auto px-6">
          <div ref={headerRef} className="sticky top-0 z-40 bg-slate-50 pt-8 pb-3">
            <div className="mb-4">
              <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-1">Admin</div>
              <h1 className="text-3xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Catalogue Management</h1>
              <p className="text-sm text-slate-600 mt-1">Add products, run bulk ops, manage banners & build combo kits.</p>
            </div>

            <div className="flex gap-1 border-b border-slate-200 flex-wrap">
              {visibleTabs.map(t => {
                const I = t.icon;
                return (
                  <button key={t.key} data-testid={`adm-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
                    className={`px-5 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-all ${activeTab === t.key ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                    <I size={14} weight={activeTab === t.key ? "fill" : "regular"} /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pt-5 pb-10">
            {activeTab === "products" && <ProductsTab user={user} topOffset={headerH} />}
            {activeTab === "brands" && <BrandMasterTab user={user} />}
            {activeTab === "bulk" && <BulkOpsTab user={user} />}
            {activeTab === "categories" && <CategoriesTab user={user} />}
            {activeTab === "tags" && <TagsTab user={user} />}
            {activeTab === "banners" && <BannersTab user={user} />}
            {activeTab === "combos" && <CombosTab user={user} />}
            {activeTab === "lead-sources" && <LeadSourcesTab user={user} />}
            {activeTab === "followup-statuses" && <FollowupStatusesTab user={user} />}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==================== PRODUCTS TAB ==================== */
const PAGE_SIZES = [25, 50, 100, 200];
const PLACEHOLDER_IMG = "/assets/placeholder-product.png";

// Compact page list with ellipsis — never renders thousands of buttons.
const buildPageList = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push("…");
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push("…");
  pages.push(total);
  return pages;
};

const ProductThumb = ({ p }) => {
  const [src, setSrc] = useState(p.image_urls?.[0] || p.image_url || PLACEHOLDER_IMG);
  return (
    <div className="w-10 h-10 shrink-0 bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden">
      <img src={src} alt="" width={40} height={40} loading="lazy"
        className="w-10 h-10 object-contain"
        onError={() => { if (src !== PLACEHOLDER_IMG) setSrc(PLACEHOLDER_IMG); }} />
    </div>
  );
};

const ProductsTab = ({ user, topOffset = 0 }) => {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [brandId, setBrandId] = useState("");
  const [category, setCategory] = useState("");
  const [active, setActive] = useState("");          // "" | "active" | "inactive"
  const [stockStatus, setStockStatus] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [userSorted, setUserSorted] = useState(false);  // true once admin clicks a column header
  const [loading, setLoading] = useState(true);       // initial only — never blanks after
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);
  const [gotoPage, setGotoPage] = useState("");
  const [editor, setEditor] = useState(null);
  const reqIdRef = useRef(0);
  const firstLoadRef = useRef(true);
  const toolbarRef = useRef(null);
  const [toolbarH, setToolbarH] = useState(0);

  // Measure the sticky toolbar so the sticky table header can dock right below
  // it (prevents the two sticky layers overlapping).
  useLayoutEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const update = () => setToolbarH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const hasFilters = !!(debouncedSearch || brandId || category || active || stockStatus);

  // Debounce free-text search (avoids a request per keystroke).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Any query/filter/sort/page-size change returns to page 1 (search only —
  // unrelated filters keep their state).
  useEffect(() => { setPage(1); }, [debouncedSearch, brandId, category, active, stockStatus, sortBy, sortOrder, pageSize]);

  // Filter option lists — fetched once, independent of the table refresh.
  useEffect(() => {
    (async () => {
      try {
        const [br, cr] = await Promise.all([
          axios.get(`${API}/catalogue/brands`, { params: { status: "active" } }),
          axios.get(`${API}/catalogue-categories`, { params: { include_inactive: true } }),
        ]);
        setBrands(br.data || []);
        setCategories(cr.data || []);
      } catch { /* filter lists are non-fatal */ }
    })();
  }, []);

  const fetchProducts = useCallback(async () => {
    const rid = ++reqIdRef.current;
    if (firstLoadRef.current) setLoading(true); else setRefreshing(true);
    setError(false);
    try {
      // While actively searching (and no explicit column sort chosen) rank by
      // match relevance; otherwise honour the selected column sort.
      const effectiveSortBy = debouncedSearch && !userSorted ? "relevance" : sortBy;
      const params = { page, page_size: pageSize, sort_by: effectiveSortBy, sort_order: sortOrder };
      if (debouncedSearch) params.search = debouncedSearch;
      if (brandId) params.brand_id = brandId;
      if (category) params.category = category;
      if (active) params.active = active === "active";
      if (stockStatus) params.stock_status = stockStatus;
      const r = await axios.get(`${API}/products/admin-paginated`, { params });
      if (rid !== reqIdRef.current) return;   // ignore stale response
      const d = r.data || {};
      setProducts(d.items || []);
      setTotalCount(d.total_count || 0);
      setFilteredCount(d.filtered_count ?? d.total_count ?? 0);
      setTotalPages(d.total_pages || 1);
      if (d.page && d.page !== page) setPage(d.page); // server clamped an out-of-range page
    } catch {
      if (rid !== reqIdRef.current) return;
      setError(true);
      setProducts([]);
    } finally {
      if (rid === reqIdRef.current) { setLoading(false); setRefreshing(false); firstLoadRef.current = false; }
    }
  }, [page, pageSize, debouncedSearch, brandId, category, active, stockStatus, sortBy, sortOrder, userSorted]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/products/${p.product_id}`);
      toast.success("Deleted");
      // If we just removed the last row of a non-first page, step back one page.
      if (products.length === 1 && page > 1) setPage((pg) => pg - 1);
      else fetchProducts();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.response?.data?.message || "Delete failed");
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.patch(`${API}/products/${p.product_id}/status?requester_role=${user.role}`, { is_active: !(p.is_active !== false) });
      toast.success(`${p.name} ${p.is_active !== false ? "disabled" : "enabled"}`);
      fetchProducts();
    } catch (e) { toast.error(e.response?.data?.detail || "Toggle failed"); }
  };

  const applySort = (field) => {
    setUserSorted(true);
    if (sortBy === field) setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSortBy(field); setSortOrder("asc"); }
  };

  const clearSearch = () => { setSearch(""); setDebouncedSearch(""); };

  const clearFilters = () => {
    setSearch(""); setDebouncedSearch(""); setBrandId(""); setCategory(""); setActive(""); setStockStatus("");
    setSortBy("name"); setSortOrder("asc"); setUserSorted(false);
  };

  const goTo = () => {
    const n = parseInt(gotoPage, 10);
    if (!Number.isNaN(n) && n >= 1 && n <= totalPages) setPage(n);
    setGotoPage("");
  };

  const start = filteredCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, filteredCount);
  const nf = (n) => (n || 0).toLocaleString("en-IN");

  const SortHead = ({ field, label, align = "left", className = "" }) => (
    <th className={`px-3 py-2.5 ${align === "right" ? "text-right" : "text-left"} ${className}`}>
      <button onClick={() => applySort(field)} data-testid={`adm-sort-${field}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider font-bold hover:text-[#002FA7] ${align === "right" ? "flex-row-reverse" : ""} ${sortBy === field ? "text-[#002FA7]" : "text-slate-500"}`}>
        {label}
        {sortBy === field && (sortOrder === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
      </button>
    </th>
  );

  return (
    <div data-testid="adm-products">
      {/* Toolbar — count, search, filters, New Product */}
      <div ref={toolbarRef} className="sticky z-30 bg-slate-50 pt-3 pb-3 border-b border-slate-100" style={{ top: topOffset }}>
        <div className="flex gap-3 items-center flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Products</div>
            <div className="text-lg font-bold text-slate-800" data-testid="adm-total-count">{nf(totalCount)} <span className="text-xs font-medium text-slate-500">total</span></div>
          </div>
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") setDebouncedSearch(search.trim()); }}
              placeholder="Search by name, SKU, model or brand…"
              className="w-full pl-9 pr-8 py-2 border border-slate-300 rounded text-sm outline-none focus:border-[#002FA7] bg-white" data-testid="adm-search" />
            {search && (
              <button onClick={clearSearch} data-testid="adm-search-clear" title="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                <X size={14} />
              </button>
            )}
          </div>
          <select value={brandId} onChange={e => setBrandId(e.target.value)} data-testid="adm-filter-brand"
            className="px-2.5 py-2 border border-slate-300 rounded text-sm bg-white outline-none focus:border-[#002FA7] max-w-[150px]">
            <option value="">All Brands</option>
            {brands.map(b => <option key={b.brand_id || b.id || b.name} value={b.brand_id || b.id}>{b.name}</option>)}
          </select>
          <select value={category} onChange={e => setCategory(e.target.value)} data-testid="adm-filter-category"
            className="px-2.5 py-2 border border-slate-300 rounded text-sm bg-white outline-none focus:border-[#002FA7] max-w-[150px]">
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.slug || c.category_id || c.name} value={c.slug || c.name}>{c.name}</option>)}
          </select>
          <select value={active} onChange={e => setActive(e.target.value)} data-testid="adm-filter-active"
            className="px-2.5 py-2 border border-slate-300 rounded text-sm bg-white outline-none focus:border-[#002FA7]">
            <option value="">Active + Inactive</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
          <select value={stockStatus} onChange={e => setStockStatus(e.target.value)} data-testid="adm-filter-stock"
            className="px-2.5 py-2 border border-slate-300 rounded text-sm bg-white outline-none focus:border-[#002FA7]">
            <option value="">Any Stock</option>
            <option value="in_stock">In Stock</option>
            <option value="out_of_stock">Out of Stock</option>
            <option value="preorder">Pre-order</option>
          </select>
          {hasFilters && (
            <button onClick={clearFilters} data-testid="adm-clear-filters"
              className="px-3 py-2 text-sm text-slate-600 hover:text-[#002FA7] border border-slate-300 rounded flex items-center gap-1">
              <X size={13} /> Clear
            </button>
          )}
          <button data-testid="adm-new-product" onClick={() => setEditor({ mode: "create", data: emptyProduct() })}
            className="bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-bold flex items-center gap-2 ml-auto">
            <Plus size={14} weight="bold" /> New Product
          </button>
        </div>
        <div className="mt-2 text-xs text-slate-500" data-testid="adm-showing-count">
          {filteredCount === 0
            ? (debouncedSearch ? "No products match your search." : hasFilters ? "No products match the current search or filters" : "No products available")
            : hasFilters
              ? <>Showing <b>{nf(start)}–{nf(end)}</b> of <b>{nf(filteredCount)}</b> matching · {nf(totalCount)} total products</>
              : <>Showing <b>{nf(start)}–{nf(end)}</b> of <b>{nf(totalCount)}</b> products</>}
        </div>
      </div>

      {/* Table — fixed geometry, sticky header, never unmounts on refresh */}
      <div className="bg-white border border-slate-200 rounded mt-3 relative">
        {refreshing && <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#002FA7]/70 animate-pulse z-30" data-testid="adm-refreshing" />}
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col style={{ width: "52px" }} />
            <col />
            <col style={{ width: "130px" }} />
            <col style={{ width: "130px" }} />
            <col style={{ width: "92px" }} />
            <col style={{ width: "92px" }} />
            <col style={{ width: "92px" }} />
            <col style={{ width: "96px" }} />
            <col style={{ width: "70px" }} />
            <col style={{ width: "110px" }} />
            <col style={{ width: "84px" }} />
          </colgroup>
          <thead className="sticky z-10 bg-slate-50 border-b border-slate-200 shadow-[0_1px_0_rgba(0,0,0,0.05)]" style={{ top: topOffset + toolbarH }}>
            <tr className="text-[11px]">
              <th className="px-3 py-2.5 text-left uppercase tracking-wider font-bold text-slate-500">SL. NO.</th>
              <SortHead field="name" label="Product" />
              <SortHead field="category" label="Category" />
              <SortHead field="model_number" label="SKU / Model" />
              <SortHead field="retail_price" label="Retail" align="right" />
              <SortHead field="dealer_price" label="Dealer" align="right" />
              <SortHead field="bulk_price" label="Bulk" align="right" />
              <SortHead field="stock_status" label="Stock" />
              <SortHead field="is_active" label="Active" />
              <th className="px-3 py-2.5 text-left uppercase tracking-wider font-bold text-slate-500">Tags</th>
              <th className="px-3 py-2.5 text-right uppercase tracking-wider font-bold text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody className={refreshing ? "opacity-50 transition-opacity" : "transition-opacity"}>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={`sk-${i}`} className="border-b border-slate-100">
                  {Array.from({ length: 11 }).map((__, j) => (
                    <td key={j} className="px-3 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>
                  ))}
                </tr>
              ))
            ) : error ? (
              <tr><td colSpan="11" className="p-10 text-center text-slate-500">
                Couldn’t load products. <button onClick={fetchProducts} className="text-[#002FA7] underline" data-testid="adm-retry">Retry</button>
              </td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan="11" className="p-10 text-center text-slate-500" data-testid="adm-empty">
                {debouncedSearch ? "No products match your search." : hasFilters ? "No products match the current search or filters" : "No products available"}
              </td></tr>
            ) : products.map((p, idx) => (
              <tr key={p.product_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`adm-row-${p.product_id}`}>
                <td className="px-3 py-3 text-slate-500 tabular-nums" data-testid={`adm-sl-${p.product_id}`}>{(page - 1) * pageSize + idx + 1}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2.5">
                    <ProductThumb p={p} />
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{p.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{p.brand_name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-xs truncate">{p.category}{p.subcategory && ` · ${p.subcategory}`}</td>
                <td className="px-3 py-3 text-xs font-mono truncate">{p.sku || "—"}<br/><span className="text-slate-500">{p.model_number}</span></td>
                <td className="px-3 py-3 text-right tabular-nums">₹{(p.retail_price || p.price || 0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-3 text-right tabular-nums">₹{(p.dealer_price || 0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-3 text-right tabular-nums">₹{(p.bulk_price || 0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-3"><StockBadge status={p.stock_status} /><div className="text-[10px] text-slate-500 mt-0.5">Qty: {p.stock_quantity ?? 0}</div></td>
                <td className="px-3 py-3">
                  <button onClick={() => toggleActive(p)} data-testid={`adm-toggle-${p.product_id}`}
                    title={p.is_active !== false ? "Disable" : "Enable"} className="inline-flex items-center">
                    {p.is_active !== false
                      ? <ToggleRight size={24} weight="fill" className="text-emerald-500" />
                      : <ToggleLeft size={24} weight="fill" className="text-slate-400" />}
                  </button>
                </td>
                <td className="px-3 py-3"><div className="flex gap-1 flex-wrap">{(p.tags || []).slice(0, 2).map(t => <span key={t} className="text-[9px] uppercase tracking-wider bg-slate-100 px-1.5 py-0.5">{t.replace(/_/g, ' ')}</span>)}</div></td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditor({ mode: "edit", data: p })} className="p-1.5 hover:bg-slate-100 text-[#002FA7]" title="Edit" data-testid={`adm-edit-${p.product_id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => remove(p)} className="p-1.5 hover:bg-red-50 text-red-600" title="Delete" data-testid={`adm-del-${p.product_id}`}><Trash size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredCount > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap mt-3 text-sm" data-testid="adm-pagination">
          <div className="text-xs text-slate-500">Showing {nf(start)}–{nf(end)} of {nf(filteredCount)} {hasFilters ? "matching" : "products"}</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="adm-prev-page"
              className="px-2.5 py-1.5 border border-slate-300 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 flex items-center gap-1">
              <CaretUp size={12} className="-rotate-90" /> Prev
            </button>
            {buildPageList(page, totalPages).map((n, i) => n === "…" ? (
              <span key={`e${i}`} className="px-1.5 text-slate-400">…</span>
            ) : (
              <button key={n} onClick={() => setPage(n)} data-testid={`adm-page-${n}`}
                className={`min-w-[32px] px-2 py-1.5 border rounded ${n === page ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-slate-300 hover:bg-slate-50"}`}>{n}</button>
            ))}
            <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="adm-next-page"
              className="px-2.5 py-1.5 border border-slate-300 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 flex items-center gap-1">
              Next <CaretDown size={12} className="-rotate-90" />
            </button>
            {totalPages > 7 && (
              <div className="flex items-center gap-1 ml-1">
                <input value={gotoPage} onChange={e => setGotoPage(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={e => e.key === "Enter" && goTo()} placeholder="Go" data-testid="adm-goto-input"
                  className="w-14 px-2 py-1.5 border border-slate-300 rounded text-sm outline-none focus:border-[#002FA7]" />
                <button onClick={goTo} data-testid="adm-goto-btn" className="px-2 py-1.5 border border-slate-300 rounded hover:bg-slate-50">Go</button>
              </div>
            )}
            <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} data-testid="adm-page-size"
              className="ml-1 px-2 py-1.5 border border-slate-300 rounded bg-white outline-none focus:border-[#002FA7]">
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
            </select>
          </div>
        </div>
      )}

      {editor && <ProductEditorModal editor={editor} setEditor={setEditor} brands={brands} setBrands={setBrands} categories={categories} user={user} onSaved={() => { setEditor(null); fetchProducts(); }} />}
    </div>
  );
};

const StockBadge = ({ status }) => {
  const cfg = {
    in_stock: { label: "In Stock", color: "bg-emerald-100 text-emerald-800" },
    out_of_stock: { label: "Out", color: "bg-red-100 text-red-800" },
    preorder: { label: "Pre-order", color: "bg-amber-100 text-amber-800" },
  }[status] || { label: status || "—", color: "bg-slate-100 text-slate-700" };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 font-bold ${cfg.color}`}>{cfg.label}</span>;
};

const emptyProduct = () => ({
  name: "", brand_id: "", category: "cctv-cameras", subcategory: "",
  model_number: "", sku: "", hsn: "", product_type: "", camera_type: "",
  resolution: "", indoor_outdoor: "", connectivity: "", night_vision_range: "",
  lens_size: "", warranty: "", stock_status: "in_stock", stock_quantity: 0, min_order_qty: 1,
  is_active: true,
  price: 0, retail_price: 0, dealer_price: 0, bulk_price: 0, offer_price: 0,
  image_url: "", image_urls: [], video_url: "", datasheet_url: "",
  description: "", features: [], specifications: {},
  key_features: [], package_contents: [], technical_specs: [], tags: [],
  audio_support: false, ai_features: [],
});

/* -------- Product Editor (full form for all 30+ fields) -------- */
const ProductEditorModal = ({ editor, setEditor, brands, setBrands, categories, user, onSaved }) => {
  const [form, setForm] = useState({ ...emptyProduct(), ...editor.data });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const upd = (patch) => setForm(f => ({ ...f, ...patch }));
  const addToList = (key, val) => upd({ [key]: [...(form[key] || []), val] });
  const removeFromList = (key, i) => upd({ [key]: form[key].filter((_, idx) => idx !== i) });

  const onImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(f => {
      if (f.size > 1.5 * 1024 * 1024) { toast.error(`${f.name} > 1.5 MB — consider a CDN URL`); return; }
      const reader = new FileReader();
      reader.onload = () => {
        upd({ image_urls: [...(form.image_urls || []), reader.result], image_url: form.image_url || reader.result });
      };
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const subcats = (categories.find(c => c.slug === form.category)?.subcategories) || [];

  const save = async () => {
    setSaveError("");
    // Client-side validation — collect ALL missing fields so the user fixes them in one pass
    const problems = [];
    if (!form.name?.trim()) problems.push("Product Name");
    if (!Number(form.retail_price) && !Number(form.price)) problems.push("Retail Price (must be > 0)");
    if (!form.category) problems.push("Category");
    if (problems.length > 0) {
      const msg = `Please fill required field(s): ${problems.join(", ")}.`;
      setSaveError(msg);
      toast.error(msg);
      // Scroll modal body back to the top so the user sees the red banner
      try { document.querySelector('[data-testid="adm-product-modal"]')?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* noop */ }
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || Number(form.retail_price) || 0,
        retail_price: Number(form.retail_price) || Number(form.price) || 0,
        dealer_price: Number(form.dealer_price) || 0,
        bulk_price: Number(form.bulk_price) || 0,
        offer_price: Number(form.offer_price) || 0,
        min_order_qty: Number(form.min_order_qty) || 1,
        stock_quantity: Math.max(0, Number(form.stock_quantity) || 0),
        is_active: form.is_active !== false,
      };
      if (editor.mode === "create") {
        await axios.post(`${API}/products?requester_role=${user?.role || ""}`, payload);
        toast.success("Product created");
      } else {
        await axios.put(`${API}/products/${form.product_id}?requester_role=${user?.role || ""}`, payload);
        toast.success("Product updated");
      }
      onSaved();
    } catch (err) {
      // Surface exact server validation message instead of generic "Save failed"
      const d = err?.response?.data;
      let msg = "Save failed";
      if (typeof d?.detail === "string") msg = d.detail;
      else if (Array.isArray(d?.detail)) {
        // FastAPI 422 validation: [{loc:[...], msg:"..."}]
        msg = d.detail.map((e) => `${(e.loc || []).slice(1).join(".") || "field"}: ${e.msg}`).join(" · ");
      } else if (err?.message) msg = err.message;
      setSaveError(msg);
      toast.error(msg);
      // Scroll modal back to top so the red banner is visible
      try { document.querySelector('[data-testid="adm-product-modal"]')?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* noop */ }
      // eslint-disable-next-line no-console
      console.error("[ProductEditor] save failed", err, d);
    }
    finally { setSaving(false); }
  };

  // Field-level required highlight
  const nameErr = saveError && !form.name?.trim();
  const priceErr = saveError && !Number(form.retail_price) && !Number(form.price);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditor(null)}>
      <div className="bg-white shadow-2xl max-w-5xl w-full my-4" onClick={e => e.stopPropagation()} data-testid="adm-product-modal">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center z-20">
          <h2 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{editor.mode === "create" ? "New" : "Edit"} Product</h2>
          <button onClick={() => setEditor(null)} className="p-2 hover:bg-slate-100 rounded-full"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-6">
          {saveError && (
            <div data-testid="product-save-error" className="flex items-start gap-2 border border-rose-300 bg-rose-50 text-rose-800 text-sm p-3">
              <Warning size={16} weight="duotone" className="shrink-0 mt-0.5" />
              <div><strong>Save failed — </strong>{saveError}</div>
            </div>
          )}
          {/* Section 1 — Basics */}
          <Section title="Basic Info">
            <Grid3>
              <Field label="Product Name *"><input data-testid="fld-name" value={form.name} onChange={e => upd({ name: e.target.value })} className={`${inp} ${nameErr ? "border-rose-500 ring-1 ring-rose-500" : ""}`} /></Field>
              <Field label="Brand">
                <select value={form.brand_id} onChange={e => upd({ brand_id: e.target.value })} className={inp} data-testid="fld-brand">
                  <option value="">— Select —</option>
                  {brands.map(b => <option key={b.brand_id} value={b.brand_id}>{b.name}</option>)}
                </select>
                <BrandQuickAdd
                  user={user}
                  onCreated={(newBrand) => {
                    // Append to dropdown + auto-select
                    setBrands?.((prev) => {
                      const exists = (prev || []).some((b) => b.brand_id === newBrand.brand_id);
                      return exists ? prev : [...(prev || []), newBrand];
                    });
                    upd({ brand_id: newBrand.brand_id });
                    toast.success(`Brand "${newBrand.name}" added and selected`);
                  }}
                />
              </Field>
              <Field label="Model Number"><input value={form.model_number} onChange={e => upd({ model_number: e.target.value })} className={inp} /></Field>
              <Field label="SKU"><input value={form.sku} onChange={e => upd({ sku: e.target.value })} className={`${inp} font-mono`} data-testid="fld-sku" /></Field>
              <Field label="HSN"><input value={form.hsn} onChange={e => upd({ hsn: e.target.value })} className={`${inp} font-mono`} /></Field>
              <Field label="Warranty"><input value={form.warranty} onChange={e => upd({ warranty: e.target.value })} placeholder="e.g. 2 years" className={inp} /></Field>
            </Grid3>
            <Field label="Description">
              <textarea value={form.description} onChange={e => upd({ description: e.target.value })} rows={3} className={inp} />
            </Field>
          </Section>

          {/* Section 2 — Classification */}
          <Section title="Classification">
            <Grid3>
              <Field label="Category *">
                <select value={form.category} onChange={e => upd({ category: e.target.value, subcategory: "" })} className={inp} data-testid="fld-category">
                  {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Subcategory">
                <select value={form.subcategory} onChange={e => upd({ subcategory: e.target.value })} className={inp}>
                  <option value="">—</option>
                  {subcats.map(s => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Product Type">
                <select value={form.product_type} onChange={e => upd({ product_type: e.target.value })} className={inp}>
                  <option value="">—</option>
                  <option value="analog">Analog</option><option value="ip">IP</option>
                  <option value="wireless">Wireless</option><option value="biometric">Biometric</option>
                  <option value="automation">Automation</option><option value="accessory">Accessory</option>
                </select>
              </Field>
              <Field label="Camera Type">
                <select value={form.camera_type} onChange={e => upd({ camera_type: e.target.value })} className={inp}>
                  <option value="">—</option>
                  <option value="dome">Dome</option><option value="bullet">Bullet</option>
                  <option value="ptz">PTZ</option><option value="cube">Cube</option><option value="fisheye">Fisheye</option>
                </select>
              </Field>
              <Field label="Resolution">
                <select value={form.resolution} onChange={e => upd({ resolution: e.target.value })} className={inp}>
                  <option value="">—</option><option value="2mp">2MP</option><option value="4mp">4MP</option>
                  <option value="5mp">5MP</option><option value="8mp">8MP</option><option value="4k">4K</option>
                </select>
              </Field>
              <Field label="Placement">
                <select value={form.indoor_outdoor} onChange={e => upd({ indoor_outdoor: e.target.value })} className={inp}>
                  <option value="">—</option><option value="indoor">Indoor</option>
                  <option value="outdoor">Outdoor</option><option value="both">Both</option>
                </select>
              </Field>
              <Field label="Connectivity">
                <select value={form.connectivity} onChange={e => upd({ connectivity: e.target.value })} className={inp}>
                  <option value="">—</option><option value="wired">Wired</option>
                  <option value="wireless">Wireless</option><option value="4g">4G</option><option value="solar">Solar</option>
                </select>
              </Field>
              <Field label="Night Vision Range"><input value={form.night_vision_range} onChange={e => upd({ night_vision_range: e.target.value })} placeholder="e.g. 30m" className={inp} /></Field>
              <Field label="Lens Size"><input value={form.lens_size} onChange={e => upd({ lens_size: e.target.value })} placeholder="e.g. 3.6mm" className={inp} /></Field>
            </Grid3>
            <Field label="AI Features (comma-separated)">
              <input value={(form.ai_features || []).join(", ")} onChange={e => upd({ ai_features: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                placeholder="human_detection, face_detection, motion_detection" className={inp} />
            </Field>
          </Section>

          {/* Section 3 — Pricing */}
          <Section title="Pricing Tiers">
            <Grid3>
              <Field label="Retail Price (₹) *"><input type="number" value={form.retail_price} onChange={e => upd({ retail_price: e.target.value, price: e.target.value })} className={`${inp} ${priceErr ? "border-rose-500 ring-1 ring-rose-500" : ""}`} data-testid="fld-retail" /></Field>
              <Field label="Dealer Price (₹)"><input type="number" value={form.dealer_price} onChange={e => upd({ dealer_price: e.target.value })} className={inp} data-testid="fld-dealer" /></Field>
              <Field label="Bulk Price (₹)"><input type="number" value={form.bulk_price} onChange={e => upd({ bulk_price: e.target.value })} className={inp} data-testid="fld-bulk" /></Field>
              <Field label="Offer Price (₹)"><input type="number" value={form.offer_price} onChange={e => upd({ offer_price: e.target.value })} placeholder="Optional" className={inp} /></Field>
              <Field label="Min Order Qty"><input type="number" value={form.min_order_qty} onChange={e => upd({ min_order_qty: e.target.value })} className={inp} /></Field>
              <Field label="Stock Status">
                <select value={form.stock_status} onChange={e => upd({ stock_status: e.target.value })} className={inp}>
                  <option value="in_stock">In Stock</option><option value="out_of_stock">Out of Stock</option><option value="preorder">Pre-order</option>
                </select>
              </Field>
              <Field label="Stock Quantity (on-hand)"><input type="number" value={form.stock_quantity ?? 0} onChange={e => upd({ stock_quantity: e.target.value })} className={inp} data-testid="fld-stock-qty" /></Field>
            </Grid3>
            <div className="mt-2 flex items-center gap-3 bg-slate-50 border border-slate-200 rounded p-3">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="fld-is-active">
                <input type="checkbox" checked={form.is_active !== false} onChange={e => upd({ is_active: e.target.checked })} className="w-4 h-4 accent-[#002FA7]" />
                <span className="text-sm font-bold">Product is Active (visible on the public catalogue)</span>
              </label>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 font-bold ${form.is_active !== false ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                {form.is_active !== false ? "Live" : "Hidden"}
              </span>
            </div>
            <div className="text-[11px] text-slate-500 bg-blue-50 border border-blue-200 rounded p-2 mt-1">💡 Qty tiers: 1-9 units → Retail · 10-49 → Dealer · 50+ → Bulk</div>
          </Section>

          {/* Section 4 — Media */}
          <Section title="Media & Uploads">
            <div className="space-y-3">
              <label className="inline-flex items-center gap-2 cursor-pointer border border-slate-300 px-4 py-2 hover:bg-slate-50 text-sm">
                <UploadSimple size={14} /> Upload Images (multi)
                <input type="file" accept="image/*" multiple className="hidden" onChange={onImageUpload} data-testid="fld-img-upload" />
              </label>
              {(form.image_urls || []).length > 0 && (
                <div className="grid grid-cols-5 gap-2">
                  {form.image_urls.map((url, i) => (
                    <div key={`img-${i}-${url.slice(-10)}`} className="relative aspect-square bg-slate-50 border border-slate-200 group">
                      <img src={url} alt="" className="w-full h-full object-contain p-1" />
                      <button onClick={() => removeFromList("image_urls", i)} className="absolute top-1 right-1 bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={10} /></button>
                    </div>
                  ))}
                </div>
              )}
              <Grid3>
                <Field label="Or Paste Image URL"><input value={form.image_url} onChange={e => upd({ image_url: e.target.value })} placeholder="https://…" className={inp} /></Field>
                <Field label="Product Video URL"><input value={form.video_url} onChange={e => upd({ video_url: e.target.value })} placeholder="YouTube URL" className={inp} /></Field>
                <Field label="Datasheet URL"><input value={form.datasheet_url} onChange={e => upd({ datasheet_url: e.target.value })} placeholder="https://cdn.com/datasheet.pdf" className={inp} /></Field>
              </Grid3>
            </div>
          </Section>

          {/* Section 5 — Key Features / Package Contents / Specs */}
          <Section title="Details">
            <ListEditor label="Key Features" items={form.key_features || []} onChange={v => upd({ key_features: v })} placeholder="e.g. 130dB True WDR" testPrefix="kf" />
            <ListEditor label="Package Contents" items={form.package_contents || []} onChange={v => upd({ package_contents: v })} placeholder="e.g. 1× Camera, 1× Mounting Bracket" testPrefix="pc" />
            <SpecsEditor specs={form.technical_specs || []} onChange={v => upd({ technical_specs: v })} />
            <Field label="Tags (comma-separated)">
              <input value={(form.tags || []).join(", ")} onChange={e => upd({ tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                placeholder="best_seller, hot_deal, new_arrival, featured" className={inp} data-testid="fld-tags" />
            </Field>
          </Section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between gap-3 z-20">
          {saveError ? (
            <div data-testid="product-save-error-bottom" className="flex items-start gap-2 text-rose-700 text-xs font-medium flex-1 min-w-0">
              <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
              <span className="truncate"><strong>Save failed:</strong> {saveError}</span>
            </div>
          ) : <div className="flex-1" />}
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setEditor(null)} className="border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-bold flex items-center gap-2 disabled:opacity-60" data-testid="adm-save-product">
              <Check size={14} weight="bold" /> {saving ? "Saving…" : (editor.mode === "create" ? "Create" : "Update")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------- Form helpers -------- */
const inp = "w-full border border-slate-300 rounded p-2 text-sm outline-none focus:border-[#002FA7] bg-white";
const Section = ({ title, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3 pb-2 border-b border-slate-200">{title}</div>
    <div className="space-y-3">{children}</div>
  </div>
);
const Grid3 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>;
const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">{label}</label>
    {children}
  </div>
);

const ListEditor = ({ label, items, onChange, placeholder, testPrefix }) => {
  const [input, setInput] = useState("");
  const add = () => { if (input.trim()) { onChange([...items, input.trim()]); setInput(""); } };
  return (
    <Field label={label}>
      <div className="flex gap-2 mb-2">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} className={inp} data-testid={`${testPrefix}-add`} />
        <button type="button" onClick={add} className="bg-slate-900 text-white px-4 text-sm">Add</button>
      </div>
      {items.length > 0 && (
        <div className="space-y-1">
          {items.map((item, i) => (
            <div key={`${testPrefix}-${i}-${String(item).slice(0, 20)}`} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 px-3 py-1.5">
              <span className="flex-1">{item}</span>
              <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-red-600 hover:bg-red-50 p-1"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </Field>
  );
};

const SpecsEditor = ({ specs, onChange }) => {
  const [row, setRow] = useState({ label: "", value: "" });
  const add = () => { if (row.label.trim() && row.value.trim()) { onChange([...specs, row]); setRow({ label: "", value: "" }); } };
  return (
    <Field label="Technical Specifications">
      <div className="grid grid-cols-[1fr_2fr_auto] gap-2 mb-2">
        <input value={row.label} onChange={e => setRow(r => ({ ...r, label: e.target.value }))} placeholder="Label (e.g. Sensor)" className={inp} />
        <input value={row.value} onChange={e => setRow(r => ({ ...r, value: e.target.value }))} placeholder="Value (e.g. 1/2.7″ CMOS)" className={inp} />
        <button type="button" onClick={add} className="bg-slate-900 text-white px-4 text-sm">Add</button>
      </div>
      {specs.length > 0 && (
        <table className="w-full text-sm border border-slate-200">
          <tbody>
            {specs.map((s, i) => (
              <tr key={`spec-${i}-${s.label}`} className="border-b border-slate-100 last:border-0">
                <td className="p-2 font-medium text-slate-600 w-1/3">{s.label}</td>
                <td className="p-2">{s.value}</td>
                <td className="p-2 text-right"><button type="button" onClick={() => onChange(specs.filter((_, idx) => idx !== i))} className="text-red-600 p-1 hover:bg-red-50"><X size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Field>
  );
};

/* ==================== BULK OPS TAB ==================== */
const BulkOpsTab = ({ user }) => {
  const [importPreview, setImportPreview] = useState([]);
  const [importMode, setImportMode] = useState("upsert");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [bulkForm, setBulkForm] = useState({ category: "", price_change_pct: "", stock_status: "" });
  const [bulkResult, setBulkResult] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { toast.error("CSV is empty"); return; }
      const headers = parseCsvLine(lines[0]);
      const rows = lines.slice(1).map(line => {
        const vals = parseCsvLine(line);
        const obj = {};
        headers.forEach((h, i) => { obj[h.trim()] = (vals[i] || "").trim(); });
        return obj;
      });
      setImportPreview(rows);
      toast.success(`${rows.length} rows parsed. Click "Import" to apply.`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const r = await axios.post(`${API}/catalogue/import-csv?requester_role=${user.role}`, { rows: importPreview, mode: importMode });
      setImportResult(r.data);
      toast.success(`Import done: ${r.data.created} created, ${r.data.updated} updated, ${r.data.skipped} skipped`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const runExport = () => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const qs = new URLSearchParams({ requester_role: user.role });
    if (bulkForm.category) qs.set("category", bulkForm.category);
    window.open(`${base}/api/catalogue/export-csv?${qs.toString()}`, "_blank");
  };

  const downloadTemplate = (format) => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    window.open(`${base}/api/catalogue/template-${format}?requester_role=${user.role}`, "_blank");
  };

  const runExportXlsx = () => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const qs = new URLSearchParams({ requester_role: user.role });
    if (bulkForm.category) qs.set("category", bulkForm.category);
    window.open(`${base}/api/catalogue/export-xlsx?${qs.toString()}`, "_blank");
  };

  const handleXlsxFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Excel file exceeds 5MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await axios.post(`${API}/catalogue/import-xlsx?requester_role=${user.role}`,
          { file_data_url: reader.result, mode: importMode });
        setImportResult(data);
        toast.success(`Excel import: ${data.created} created, ${data.updated} updated, ${data.skipped} skipped`);
      } catch (err) { toast.error(err?.response?.data?.detail || "Excel import failed"); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const runBulkUpdate = async () => {
    const body = { filters: { category: bulkForm.category }, tags_add: [], tags_remove: [] };
    if (bulkForm.price_change_pct) body.price_change_pct = Number(bulkForm.price_change_pct);
    if (bulkForm.stock_status) body.stock_status = bulkForm.stock_status;
    try {
      const r = await axios.post(`${API}/catalogue/bulk-update?requester_role=${user.role}`, body);
      setBulkResult(r.data);
      toast.success(`${r.data.updated} product(s) updated`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="adm-bulk">
      <div className="bg-white border border-slate-200 p-5 rounded">
        <h2 className="text-lg font-bold mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>📥 Import Products (CSV)</h2>
        <p className="text-xs text-slate-600 mb-3">Upload a CSV with columns: name, brand_name, category, sku, model_number, retail_price, dealer_price, bulk_price, description, tags (pipe-separated), key_features (pipe-separated), etc.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 cursor-pointer border border-slate-300 px-4 py-2 hover:bg-slate-50 text-sm">
            <UploadSimple size={14} /> Choose CSV
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} data-testid="csv-upload" />
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer border border-emerald-500 text-emerald-700 px-4 py-2 hover:bg-emerald-50 text-sm font-bold">
            <FileXls size={14} weight="fill" /> Upload Excel (.xlsx)
            <input type="file" accept=".xlsx" className="hidden" onChange={handleXlsxFile} data-testid="xlsx-upload" />
          </label>
          <select value={importMode} onChange={e => setImportMode(e.target.value)} className="border border-slate-300 rounded p-2 text-sm bg-white">
            <option value="upsert">Upsert (update if SKU exists)</option>
            <option value="create_only">Create only</option>
          </select>
          {importPreview.length > 0 && (
            <button onClick={runImport} disabled={importing} className="bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-bold disabled:opacity-60" data-testid="csv-import-run">
              {importing ? "Importing…" : `Import ${importPreview.length} Rows`}
            </button>
          )}
          <a href="/assets/sample-products.csv" download className="text-xs text-[#002FA7] hover:underline ml-auto">📄 Legacy sample CSV</a>
        </div>
        <div className="mt-3 bg-blue-50 border border-blue-200 px-3 py-2 rounded flex items-center gap-2 flex-wrap text-xs">
          <span className="font-bold text-blue-900">First time uploading?</span>
          <span className="text-blue-800">Download our branded template (includes is_active + stock_quantity + tooltips + example row + how-to sheet):</span>
          <button onClick={() => downloadTemplate("xlsx")} data-testid="template-xlsx-btn" className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 font-bold text-[11px]">
            <FileXls size={12} weight="fill" /> Excel Template
          </button>
          <button onClick={() => downloadTemplate("csv")} data-testid="template-csv-btn" className="inline-flex items-center gap-1 bg-slate-700 hover:bg-slate-800 text-white px-2.5 py-1 font-bold text-[11px]">
            <DownloadSimple size={12} /> CSV Template
          </button>
        </div>
        {importResult && (
          <div className="mt-3 border border-emerald-200 bg-emerald-50 rounded p-3 text-sm">
            <b>Result:</b> {importResult.created} created · {importResult.updated} updated · {importResult.skipped} skipped
            {importResult.errors?.length > 0 && (
              <details className="mt-2"><summary className="cursor-pointer text-amber-700">{importResult.errors.length} errors</summary>
                <ul className="text-[11px] mt-1">{importResult.errors.map(e => <li key={`err-${e.row}`}>Row {e.row}: {e.reason}</li>)}</ul>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 p-5 rounded">
        <h2 className="text-lg font-bold mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>📤 Export & Bulk Update</h2>
        <Grid3>
          <Field label="Filter by Category (optional)">
            <input value={bulkForm.category} onChange={e => setBulkForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. cctv-cameras" className={inp} />
          </Field>
          <Field label="Price Change %">
            <input type="number" value={bulkForm.price_change_pct} onChange={e => setBulkForm(f => ({ ...f, price_change_pct: e.target.value }))} placeholder="−10 for 10% off" className={inp} data-testid="bulk-price-pct" />
          </Field>
          <Field label="Stock Status">
            <select value={bulkForm.stock_status} onChange={e => setBulkForm(f => ({ ...f, stock_status: e.target.value }))} className={inp}>
              <option value="">— no change —</option><option value="in_stock">In Stock</option>
              <option value="out_of_stock">Out of Stock</option><option value="preorder">Pre-order</option>
            </select>
          </Field>
        </Grid3>
        <div className="flex gap-3 mt-4 flex-wrap">
          <button onClick={runExport} className="bg-slate-900 text-white hover:bg-slate-700 px-4 py-2 text-sm font-bold flex items-center gap-2" data-testid="csv-export"><DownloadSimple size={14} /> Export CSV</button>
          <button onClick={runExportXlsx} className="bg-emerald-600 text-white hover:bg-emerald-700 px-4 py-2 text-sm font-bold flex items-center gap-2" data-testid="xlsx-export"><FileXls size={14} weight="fill" /> Export Excel (.xlsx)</button>
          <button onClick={runBulkUpdate} className="bg-amber-500 text-white hover:bg-amber-600 px-4 py-2 text-sm font-bold flex items-center gap-2" data-testid="bulk-update-run"><Lightning size={14} /> Apply Bulk Update</button>
        </div>
        {bulkResult && <div className="mt-3 text-sm bg-amber-50 border border-amber-200 rounded p-2">Updated {bulkResult.updated} product(s)</div>}
      </div>

      <BulkStockPanel user={user} />
    </div>
  );
};

// Minimal CSV line parser (handles quoted fields with commas)
const parseCsvLine = (line) => {
  const out = [];
  let cur = "", inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { inQ = false; }
      else { cur += ch; }
    } else {
      if (ch === '"') { inQ = true; }
      else if (ch === ',') { out.push(cur); cur = ""; }
      else { cur += ch; }
    }
  }
  out.push(cur);
  return out;
};

/* ==================== BANNERS TAB ==================== */
const BannersTab = ({ user }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setBanners((await axios.get(`${API}/banners`)).data || []); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (b) => {
    if (!window.confirm(`Delete banner "${b.title}"?`)) return;
    try { await axios.delete(`${API}/banners/${b.banner_id}?requester_role=${user.role}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="adm-banners">
      <div className="flex justify-between mb-4">
        <p className="text-sm text-slate-600">Manage homepage / category / offer banners shown across the site.</p>
        <button onClick={() => setEditor({ mode: "create", data: emptyBanner() })} className="bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-bold flex items-center gap-2" data-testid="adm-new-banner">
          <Plus size={14} weight="bold" /> New Banner
        </button>
      </div>
      {loading ? <div className="py-10 flex justify-center"><div className="loading-spinner" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {banners.length === 0 ? <p className="text-sm text-slate-500 col-span-full py-10 text-center">No banners yet.</p> :
            banners.map(b => (
              <div key={b.banner_id} className="bg-white border border-slate-200 rounded overflow-hidden" data-testid={`banner-card-${b.banner_id}`}>
                <div className="aspect-[16/6] bg-slate-100 relative">
                  {b.image_url ? <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400"><ImageIcon size={30} /></div>}
                  <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider bg-white px-2 py-0.5 font-bold">{b.banner_type}</span>
                  {!b.active && <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider bg-red-600 text-white px-2 py-0.5 font-bold">Inactive</span>}
                </div>
                <div className="p-3">
                  <h3 className="font-bold text-sm">{b.title}</h3>
                  <p className="text-xs text-slate-500 truncate">{b.subtitle}</p>
                  <div className="flex gap-1 mt-2 pt-2 border-t border-slate-100">
                    <button onClick={() => setEditor({ mode: "edit", data: b })} className="flex-1 text-[#002FA7] hover:bg-slate-50 text-xs font-bold py-1"><PencilSimple size={12} className="inline mr-1" />Edit</button>
                    <button onClick={() => remove(b)} className="flex-1 text-red-600 hover:bg-red-50 text-xs font-bold py-1"><Trash size={12} className="inline mr-1" />Delete</button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
      {editor && <BannerEditor editor={editor} setEditor={setEditor} user={user} onSaved={() => { setEditor(null); load(); }} />}
    </div>
  );
};

const emptyBanner = () => ({ banner_type: "homepage", title: "", subtitle: "", image_url: "", link_url: "", position: 0, active: true, category_slug: "", starts_at: "", ends_at: "", accent_color: "" });

const BannerEditor = ({ editor, setEditor, user, onSaved }) => {
  const [form, setForm] = useState({ ...emptyBanner(), ...editor.data });
  const [saving, setSaving] = useState(false);
  const upd = (patch) => setForm(f => ({ ...f, ...patch }));
  const onUpload = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Max 2 MB"); return; }
    const reader = new FileReader(); reader.onload = () => upd({ image_url: reader.result }); reader.readAsDataURL(f); e.target.value = "";
  };
  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      if (editor.mode === "create") await axios.post(`${API}/banners?requester_role=${user.role}`, form);
      else await axios.put(`${API}/banners/${form.banner_id}?requester_role=${user.role}`, form);
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditor(null)}>
      <div className="bg-white shadow-2xl max-w-2xl w-full my-4" onClick={e => e.stopPropagation()} data-testid="banner-editor">
        <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-lg font-bold">{editor.mode === "create" ? "New" : "Edit"} Banner</h2>
          <button onClick={() => setEditor(null)} className="p-2 hover:bg-slate-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Grid3>
            <Field label="Type"><select value={form.banner_type} onChange={e => upd({ banner_type: e.target.value })} className={inp}><option value="homepage">Homepage</option><option value="category">Category</option><option value="offer">Offer</option></select></Field>
            <Field label="Position"><input type="number" value={form.position} onChange={e => upd({ position: Number(e.target.value) })} className={inp} /></Field>
            <Field label="Active"><select value={form.active ? "1" : "0"} onChange={e => upd({ active: e.target.value === "1" })} className={inp}><option value="1">Active</option><option value="0">Inactive</option></select></Field>
          </Grid3>
          <Field label="Title *"><input value={form.title} onChange={e => upd({ title: e.target.value })} className={inp} data-testid="banner-title" /></Field>
          <Field label="Subtitle"><input value={form.subtitle} onChange={e => upd({ subtitle: e.target.value })} className={inp} /></Field>
          <Field label="Banner Image">
            <label className="inline-flex items-center gap-2 cursor-pointer border border-slate-300 px-3 py-2 hover:bg-slate-50 text-sm">
              <UploadSimple size={14} /> Upload image (≤2 MB)
              <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
            </label>
            {form.image_url && <img src={form.image_url} alt="" className="mt-2 max-h-40 border border-slate-200" />}
          </Field>
          <Field label="Link URL"><input value={form.link_url} onChange={e => upd({ link_url: e.target.value })} placeholder="/products?tag=hot_deal" className={inp} /></Field>
          {form.banner_type === "category" && <Field label="Category Slug"><input value={form.category_slug} onChange={e => upd({ category_slug: e.target.value })} placeholder="cctv-cameras" className={inp} /></Field>}
          <Grid3>
            <Field label="Starts At"><input type="date" value={form.starts_at?.slice(0, 10) || ""} onChange={e => upd({ starts_at: e.target.value })} className={inp} /></Field>
            <Field label="Ends At"><input type="date" value={form.ends_at?.slice(0, 10) || ""} onChange={e => upd({ ends_at: e.target.value })} className={inp} /></Field>
            <Field label="Accent Colour"><input type="color" value={form.accent_color || "#002FA7"} onChange={e => upd({ accent_color: e.target.value })} className="w-full h-9 border border-slate-300 rounded" /></Field>
          </Grid3>
        </div>
        <div className="border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
          <button onClick={() => setEditor(null)} className="border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-bold disabled:opacity-60" data-testid="banner-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
};

/* ==================== COMBOS TAB ==================== */
const CombosTab = ({ user }) => {
  const [combos, setCombos] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        axios.get(`${API}/combos`),
        axios.get(`${API}/products?limit=200`),
      ]);
      setCombos(c.data || []);
      setProducts((p.data || []).filter(x => x.product_type !== "combo"));
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="adm-combos">
      <div className="flex justify-between mb-4">
        <p className="text-sm text-slate-600">Bundle products into combo kits (e.g. 4-camera kit, smart home starter).</p>
        <button onClick={() => setEditorOpen(true)} className="bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-bold flex items-center gap-2" data-testid="adm-new-combo">
          <Plus size={14} weight="bold" /> New Combo
        </button>
      </div>
      {loading ? <div className="py-10 flex justify-center"><div className="loading-spinner" /></div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {combos.length === 0 ? <p className="text-sm text-slate-500 col-span-full py-10 text-center">No combos yet.</p> :
            combos.map(c => (
              <div key={c.product_id} className="bg-white border-2 border-amber-300 rounded p-4" data-testid={`combo-card-${c.product_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Stack size={18} className="text-amber-600" weight="fill" />
                  <h3 className="font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{c.name}</h3>
                </div>
                <p className="text-xs text-slate-500 mb-3">{c.description}</p>
                <ul className="text-xs space-y-1 mb-3">
                  {(c.components || []).map((comp, i) => (
                    <li key={`combo-${c.product_id}-comp-${i}`} className="flex items-center gap-2">
                      <span className="text-amber-600">×{comp.quantity}</span> <span>{comp.product_name}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-slate-100 pt-2 flex items-baseline gap-2">
                  <span className="text-xl font-bold text-[#002FA7]">₹{(c.price || 0).toLocaleString('en-IN')}</span>
                  {c.combo_saving > 0 && <span className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 font-bold">Save ₹{c.combo_saving.toLocaleString('en-IN')}</span>}
                </div>
              </div>
            ))}
        </div>
      )}
      {editorOpen && <ComboEditor products={products} user={user} onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); load(); }} />}
    </div>
  );
};

const ComboEditor = ({ products, user, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: "", description: "", category: "cctv-cameras", image_url: "", bundle_price: 0, components: [], tags: ["combo"] });
  const [saving, setSaving] = useState(false);
  const [pickId, setPickId] = useState("");
  const [pickQty, setPickQty] = useState(1);

  const retailTotal = useMemo(() => form.components.reduce((sum, c) => {
    const p = products.find(x => x.product_id === c.product_id);
    return sum + (p ? (p.retail_price || p.price || 0) * c.quantity : 0);
  }, 0), [form.components, products]);

  const addComp = () => {
    if (!pickId) return;
    setForm(f => ({ ...f, components: [...f.components, { product_id: pickId, quantity: Number(pickQty) }] }));
    setPickId(""); setPickQty(1);
  };
  const removeComp = (i) => setForm(f => ({ ...f, components: f.components.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim() || form.components.length === 0 || !form.bundle_price) { toast.error("Name, components & bundle price required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/combos?requester_role=${user.role}`, form);
      toast.success("Combo created"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white shadow-2xl max-w-3xl w-full my-4" onClick={e => e.stopPropagation()} data-testid="combo-editor">
        <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-lg font-bold">New Combo Kit</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Combo Name *"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. 4-Camera Home Security Kit" className={inp} data-testid="combo-name" /></Field>
          <Field label="Description"><textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className={inp} /></Field>

          <Field label="Add Components">
            <div className="grid grid-cols-[1fr_100px_auto] gap-2">
              <select value={pickId} onChange={e => setPickId(e.target.value)} className={inp} data-testid="combo-pick-product">
                <option value="">— Select product —</option>
                {products.map(p => <option key={p.product_id} value={p.product_id}>{p.name} · ₹{(p.retail_price || p.price || 0).toLocaleString('en-IN')}</option>)}
              </select>
              <input type="number" min="1" value={pickQty} onChange={e => setPickQty(e.target.value)} className={inp} />
              <button type="button" onClick={addComp} className="bg-slate-900 text-white px-4 text-sm">Add</button>
            </div>
            {form.components.length > 0 && (
              <div className="mt-2 border border-slate-200 rounded">
                {form.components.map((c, i) => {
                  const p = products.find(x => x.product_id === c.product_id);
                  return (
                    <div key={`comp-${i}-${c.product_id}`} className="flex items-center gap-3 p-2 border-b border-slate-100 last:border-0">
                      <span className="bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-bold">×{c.quantity}</span>
                      <span className="flex-1 text-sm">{p?.name || c.product_id}</span>
                      <span className="text-xs text-slate-500">₹{((p?.retail_price || p?.price || 0) * c.quantity).toLocaleString('en-IN')}</span>
                      <button type="button" onClick={() => removeComp(i)} className="text-red-600 p-1 hover:bg-red-50"><X size={12} /></button>
                    </div>
                  );
                })}
              </div>
            )}
          </Field>

          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
            <div className="flex justify-between py-0.5"><span className="text-slate-600">Retail total (components)</span><span>₹{retailTotal.toLocaleString('en-IN')}</span></div>
            <div className="flex justify-between py-0.5 mt-1">
              <span className="text-slate-600 font-bold">Bundle Price *</span>
              <input type="number" value={form.bundle_price} onChange={e => setForm(f => ({ ...f, bundle_price: Number(e.target.value) }))} className="w-32 text-right border border-slate-300 rounded p-1 text-sm" data-testid="combo-bundle-price" />
            </div>
            {retailTotal > 0 && form.bundle_price > 0 && form.bundle_price < retailTotal && (
              <div className="text-emerald-700 font-bold mt-1">Customer saves ₹{(retailTotal - form.bundle_price).toLocaleString('en-IN')} vs buying separately</div>
            )}
          </div>
        </div>
        <div className="border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-bold disabled:opacity-60" data-testid="combo-save">{saving ? "Saving…" : "Create Combo"}</button>
        </div>
      </div>
    </div>
  );
};

/* ==================== BULK STOCK PANEL (Bulk Ops tab) ==================== */
const BulkStockPanel = ({ user }) => {
  const [rows, setRows] = useState([{ sku: "", stock_quantity: "", stock_status: "" }]);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const addRow = () => setRows(r => [...r, { sku: "", stock_quantity: "", stock_status: "" }]);
  const updRow = (i, patch) => setRows(r => r.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  const delRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));

  const handleCsv = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { toast.error("CSV empty"); return; }
      const headers = parseCsvLine(lines[0]).map(h => h.trim().toLowerCase());
      const parsed = lines.slice(1).map(line => {
        const vals = parseCsvLine(line);
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (vals[i] || "").trim(); });
        return { sku: obj.sku || "", product_id: obj.product_id || "", stock_quantity: obj.stock_quantity || obj.qty || "", stock_status: obj.stock_status || "" };
      });
      setRows(parsed);
      toast.success(`${parsed.length} rows loaded`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const run = async () => {
    const updates = rows.filter(r => (r.sku || r.product_id) && (r.stock_quantity !== "" || r.stock_status))
      .map(r => ({
        sku: r.sku || undefined, product_id: r.product_id || undefined,
        stock_quantity: r.stock_quantity !== "" ? Number(r.stock_quantity) : undefined,
        stock_status: r.stock_status || undefined,
      }));
    if (updates.length === 0) { toast.error("Add at least one row with SKU + stock"); return; }
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/catalogue/bulk-stock-update?requester_role=${user.role}`, { updates });
      setResult(data);
      toast.success(`${data.updated} product(s) updated`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk stock update failed"); }
    finally { setRunning(false); }
  };

  return (
    <div className="bg-white border border-slate-200 p-5 rounded" data-testid="bulk-stock-panel">
      <h2 className="text-lg font-bold mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>📦 Bulk Stock Quantity Update</h2>
      <p className="text-xs text-slate-600 mb-3">Update on-hand stock quantities for multiple products at once. Match by SKU or product_id. Upload a CSV or use the rows below.</p>
      <div className="flex gap-2 mb-3 flex-wrap">
        <label className="inline-flex items-center gap-2 cursor-pointer border border-slate-300 px-3 py-1.5 hover:bg-slate-50 text-xs">
          <UploadSimple size={12} /> Load CSV (sku, stock_quantity, stock_status)
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleCsv} data-testid="bulk-stock-csv" />
        </label>
        <button onClick={addRow} className="text-xs border border-slate-300 px-3 py-1.5 hover:bg-slate-50 flex items-center gap-1"><Plus size={12} /> Add Row</button>
      </div>
      <div className="border border-slate-200 overflow-x-auto mb-3">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr className="text-left uppercase tracking-wider text-slate-500">
              <th className="p-2 font-bold">SKU</th>
              <th className="p-2 font-bold">Stock Qty</th>
              <th className="p-2 font-bold">Status</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`sr-${i}`} className="border-t border-slate-100">
                <td className="p-1.5"><input value={row.sku} onChange={e => updRow(i, { sku: e.target.value })} placeholder="SKU or product_id" className={inp} data-testid={`bs-sku-${i}`} /></td>
                <td className="p-1.5"><input type="number" value={row.stock_quantity} onChange={e => updRow(i, { stock_quantity: e.target.value })} className={inp} data-testid={`bs-qty-${i}`} /></td>
                <td className="p-1.5">
                  <select value={row.stock_status} onChange={e => updRow(i, { stock_status: e.target.value })} className={inp}>
                    <option value="">— auto —</option>
                    <option value="in_stock">In Stock</option><option value="out_of_stock">Out of Stock</option><option value="preorder">Pre-order</option>
                  </select>
                </td>
                <td className="p-1.5 text-right"><button onClick={() => delRow(i)} className="text-red-600 hover:bg-red-50 p-1"><Trash size={12} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={run} disabled={running} className="bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-bold disabled:opacity-60" data-testid="bulk-stock-run">
        {running ? "Applying…" : `Apply Stock Update (${rows.length} rows)`}
      </button>
      {result && (
        <div className="mt-3 border border-emerald-200 bg-emerald-50 rounded p-3 text-sm">
          <b>{result.updated}</b> product(s) updated.
          {result.errors?.length > 0 && (
            <details className="mt-2"><summary className="cursor-pointer text-amber-700">{result.errors.length} errors</summary>
              <ul className="text-[11px] mt-1">{result.errors.map((e, i) => <li key={`e-${i}`}>{e}</li>)}</ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

/* ==================== CATEGORIES TAB ==================== */
const CategoriesTab = ({ user }) => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // { mode, data, parent_slug }
  const [dragIndex, setDragIndex] = useState(null);
  const [dragBucket, setDragBucket] = useState(null); // null=parents, or parent_slug for subs
  const [savingOrder, setSavingOrder] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/catalogue-categories`, { params: { include_inactive: true } });
      setTree(data || []);
    } catch { toast.error("Failed to load categories"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const persistOrder = async (slugs) => {
    setSavingOrder(true);
    try {
      await axios.post(`${API}/catalogue-categories/reorder?requester_role=${user.role}`, { slugs });
      toast.success("Order saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Reorder failed"); load(); }
    finally { setSavingOrder(false); }
  };

  const handleParentDrop = (toIdx) => {
    if (dragBucket !== null || dragIndex === null || dragIndex === toIdx) { setDragIndex(null); setDragBucket(null); return; }
    const next = [...tree];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(toIdx, 0, moved);
    setTree(next);
    setDragIndex(null);
    setDragBucket(null);
    persistOrder(next.map(p => p.slug));
  };

  const handleSubDrop = (parentSlug, toIdx) => {
    if (dragBucket !== parentSlug || dragIndex === null || dragIndex === toIdx) { setDragIndex(null); setDragBucket(null); return; }
    const next = tree.map(p => {
      if (p.slug !== parentSlug) return p;
      const subs = [...(p.subcategories || [])];
      const [moved] = subs.splice(dragIndex, 1);
      subs.splice(toIdx, 0, moved);
      return { ...p, subcategories: subs };
    });
    setTree(next);
    setDragIndex(null);
    setDragBucket(null);
    const parent = next.find(p => p.slug === parentSlug);
    persistOrder((parent.subcategories || []).map(s => s.slug));
  };

  const toggleActive = async (cat) => {
    try {
      await axios.put(`${API}/catalogue-categories/${cat.slug}?requester_role=${user.role}`, { is_active: !(cat.is_active !== false) });
      toast.success("Toggled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"${cat.subcategories?.length ? ` and its ${cat.subcategories.length} subcategories` : ""}?`)) return;
    try { await axios.delete(`${API}/catalogue-categories/${cat.slug}?requester_role=${user.role}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <div className="py-10 flex justify-center"><div className="loading-spinner" /></div>;
  return (
    <div data-testid="adm-categories">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Categories & Subcategories</h2>
          <p className="text-xs text-slate-600">Drag the handle (⋮⋮) to reorder — changes save automatically. {savingOrder && <span className="text-[#002FA7] font-bold">Saving order…</span>}</p>
        </div>
        <button onClick={() => setEditor({ mode: "create", data: { slug: "", name: "", icon: "Package", image_url: "", sort_order: 0, is_active: true, parent_slug: null } })}
          className="bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-bold flex items-center gap-2" data-testid="cat-new-parent"><Plus size={14} /> New Category</button>
      </div>

      <div className="space-y-3">
        {tree.map((parent, pIdx) => (
          <div key={parent.slug} className={`bg-white border border-slate-200 rounded transition-opacity ${dragBucket === null && dragIndex === pIdx ? "opacity-50" : ""}`}
            onDragOver={(e) => { if (dragBucket === null) e.preventDefault(); }}
            onDrop={() => handleParentDrop(pIdx)}
            data-testid={`cat-parent-${parent.slug}`}>
            <div className="flex items-center justify-between p-3 border-b border-slate-100">
              <div className="flex items-center gap-3 min-w-0">
                <span draggable onDragStart={() => { setDragIndex(pIdx); setDragBucket(null); }} onDragEnd={() => { setDragIndex(null); setDragBucket(null); }}
                  className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-[#002FA7] select-none text-lg font-black leading-none"
                  title="Drag to reorder" data-testid={`cat-drag-${parent.slug}`}>⋮⋮</span>
                {parent.image_url ? <img src={parent.image_url} alt="" className="w-10 h-10 object-cover bg-slate-50 border border-slate-200" /> : <div className="w-10 h-10 bg-slate-100 flex items-center justify-center text-slate-400"><Folder size={18} /></div>}
                <div>
                  <div className="font-bold">{parent.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{parent.slug} · {parent.subcategories?.length || 0} subcategories</div>
                </div>
                {parent.is_active === false && <span className="text-[9px] uppercase tracking-wider bg-slate-200 text-slate-600 px-1.5 py-0.5 font-bold">Disabled</span>}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => toggleActive(parent)} title={parent.is_active !== false ? "Disable" : "Enable"}>
                  {parent.is_active !== false ? <ToggleRight size={22} weight="fill" className="text-emerald-500" /> : <ToggleLeft size={22} weight="fill" className="text-slate-400" />}
                </button>
                <button onClick={() => setEditor({ mode: "create", data: { slug: "", name: "", icon: "Tag", image_url: "", sort_order: 0, is_active: true, parent_slug: parent.slug } })}
                  className="text-xs text-[#002FA7] hover:underline font-bold px-2">+ Sub</button>
                <button onClick={() => setEditor({ mode: "edit", data: parent })} className="p-1.5 hover:bg-slate-100 text-[#002FA7]" title="Edit"><PencilSimple size={14} /></button>
                <button onClick={() => remove(parent)} className="p-1.5 hover:bg-red-50 text-red-600" title="Delete"><Trash size={14} /></button>
              </div>
            </div>
            {parent.subcategories?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-3">
                {parent.subcategories.map((sub, sIdx) => (
                  <div key={sub.slug} className={`border border-slate-200 px-2.5 py-2 flex items-center justify-between transition-opacity ${dragBucket === parent.slug && dragIndex === sIdx ? "opacity-50" : ""}`}
                    onDragOver={(e) => { if (dragBucket === parent.slug) e.preventDefault(); }}
                    onDrop={() => handleSubDrop(parent.slug, sIdx)}
                    data-testid={`cat-sub-${sub.slug}`}>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span draggable onDragStart={() => { setDragIndex(sIdx); setDragBucket(parent.slug); }} onDragEnd={() => { setDragIndex(null); setDragBucket(null); }}
                        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-[#002FA7] select-none text-sm font-black leading-none">⋮⋮</span>
                      <div className="min-w-0">
                        <div className="text-sm font-bold truncate">{sub.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono truncate">{sub.slug}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button onClick={() => toggleActive(sub)}>{sub.is_active !== false ? <ToggleRight size={18} weight="fill" className="text-emerald-500" /> : <ToggleLeft size={18} weight="fill" className="text-slate-400" />}</button>
                      <button onClick={() => setEditor({ mode: "edit", data: sub })} className="p-1 hover:bg-slate-100 text-[#002FA7]"><PencilSimple size={12} /></button>
                      <button onClick={() => remove(sub)} className="p-1 hover:bg-red-50 text-red-600"><Trash size={12} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {editor && <CategoryEditorModal editor={editor} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} user={user} />}
    </div>
  );
};

const CategoryEditorModal = ({ editor, onClose, onSaved, user }) => {
  const [form, setForm] = useState({ ...editor.data });
  const [saving, setSaving] = useState(false);

  const onImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) { toast.error("Image > 1.5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, image_url: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    if (editor.mode === "create" && !form.slug?.trim()) {
      form.slug = form.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    }
    setSaving(true);
    try {
      if (editor.mode === "create") {
        await axios.post(`${API}/catalogue-categories?requester_role=${user.role}`, form);
        toast.success("Category created");
      } else {
        const { slug, ...patch } = form;
        await axios.put(`${API}/catalogue-categories/${slug}?requester_role=${user.role}`, patch);
        toast.success("Category updated");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3" data-testid="cat-editor">
      <div className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
          <h3 className="text-base font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{editor.mode === "create" ? "New Category" : "Edit Category"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Category Name *"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inp} data-testid="cat-name" /></Field>
          <Field label={`Slug ${editor.mode === "edit" ? "(locked)" : "(auto-generates if blank)"}`}>
            <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") }))} disabled={editor.mode === "edit"} className={inp} data-testid="cat-slug" placeholder="auto-generated-from-name" />
          </Field>
          {form.parent_slug && <div className="text-xs text-slate-500">Subcategory of <b>{form.parent_slug}</b></div>}
          <Field label="Category Image (optional)">
            <div className="flex items-center gap-3">
              {form.image_url && <img src={form.image_url} alt="" className="w-16 h-16 object-cover bg-slate-50 border border-slate-200" />}
              <label className="border border-slate-300 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                Upload Image <input type="file" accept="image/*" className="hidden" onChange={onImageUpload} data-testid="cat-image-upload" />
              </label>
              {form.image_url && <button onClick={() => setForm(f => ({ ...f, image_url: "" }))} className="text-xs text-red-600 hover:underline">Clear</button>}
            </div>
          </Field>
          <Grid3>
            <Field label="Sort Order"><input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: Number(e.target.value) || 0 }))} className={inp} /></Field>
            <Field label="Icon (Phosphor name)"><input value={form.icon} onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} className={inp} /></Field>
            <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={form.is_active !== false} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-[#002FA7]" /> Active</label>
          </Grid3>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-3 flex justify-end gap-2">
          <button onClick={onClose} className="border border-slate-300 px-4 py-2 text-sm font-bold hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-bold disabled:opacity-60" data-testid="cat-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
};

/* ==================== TAGS TAB ==================== */
const TagsTab = ({ user }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/catalogue-tags`, { params: { include_inactive: true } });
      setTags(data || []);
    } catch { toast.error("Failed to load tags"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (t) => {
    try {
      await axios.put(`${API}/catalogue-tags/${t.slug}?requester_role=${user.role}`, { is_active: !(t.is_active !== false) });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tag "${t.name}"? It will be detached from ${t.usage_count} product(s).`)) return;
    try { await axios.delete(`${API}/catalogue-tags/${t.slug}?requester_role=${user.role}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <div className="py-10 flex justify-center"><div className="loading-spinner" /></div>;
  return (
    <div data-testid="adm-tags">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Product Tags</h2>
          <p className="text-xs text-slate-600">Consistent labels across the catalogue (Best Seller, Hot Deal, New Arrival, etc.).</p>
        </div>
        <button onClick={() => setEditor({ mode: "create", data: { slug: "", name: "", color: "#002FA7", is_active: true } })}
          className="bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-bold flex items-center gap-2" data-testid="tag-new"><Plus size={14} /> New Tag</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {tags.map(t => (
          <div key={t.slug} className="bg-white border border-slate-200 p-3 flex items-center gap-3" data-testid={`tag-row-${t.slug}`}>
            <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">{t.name}</div>
              <div className="text-[10px] text-slate-500 font-mono">{t.slug} · used by {t.usage_count} product{t.usage_count !== 1 ? "s" : ""}</div>
            </div>
            {t.is_active === false && <span className="text-[9px] uppercase tracking-wider bg-slate-200 text-slate-600 px-1.5 py-0.5 font-bold">Hidden</span>}
            <button onClick={() => toggleActive(t)} title={t.is_active !== false ? "Disable" : "Enable"}>
              {t.is_active !== false ? <ToggleRight size={20} weight="fill" className="text-emerald-500" /> : <ToggleLeft size={20} weight="fill" className="text-slate-400" />}
            </button>
            <button onClick={() => setEditor({ mode: "edit", data: t })} className="p-1 hover:bg-slate-100 text-[#002FA7]"><PencilSimple size={12} /></button>
            <button onClick={() => remove(t)} className="p-1 hover:bg-red-50 text-red-600"><Trash size={12} /></button>
          </div>
        ))}
      </div>

      {editor && <TagEditorModal editor={editor} user={user} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); load(); }} />}
    </div>
  );
};

const TagEditorModal = ({ editor, onClose, onSaved, user }) => {
  const [form, setForm] = useState({ ...editor.data });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editor.mode === "create") {
        await axios.post(`${API}/catalogue-tags?requester_role=${user.role}`, form);
        toast.success("Tag created");
      } else {
        const { slug, usage_count, ...patch } = form;
        await axios.put(`${API}/catalogue-tags/${slug}?requester_role=${user.role}`, patch);
        toast.success("Tag updated");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-3" data-testid="tag-editor">
      <div className="bg-white w-full max-w-sm shadow-2xl">
        <div className="border-b border-slate-200 p-4 flex items-center justify-between">
          <h3 className="text-base font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>{editor.mode === "create" ? "New Tag" : "Edit Tag"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Tag Name *"><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inp} data-testid="tag-name" /></Field>
          <Field label={`Slug ${editor.mode === "edit" ? "(locked)" : "(auto)"}`}>
            <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, "_") }))} disabled={editor.mode === "edit"} className={inp} placeholder="auto-from-name" />
          </Field>
          <Field label="Color">
            <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} className="w-full h-10 border border-slate-300 rounded cursor-pointer" />
          </Field>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active !== false} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="w-4 h-4 accent-[#002FA7]" /> Active</label>
        </div>
        <div className="border-t border-slate-200 p-3 flex justify-end gap-2">
          <button onClick={onClose} className="border border-slate-300 px-4 py-2 text-sm font-bold hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-bold disabled:opacity-60" data-testid="tag-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
};

export default AdminCatalogue;
